import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpDown, X, Loader2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { PublicKey } from '@solana/web3.js';

import { cn } from '@/lib/utils';
import { useUserProgress } from '@/hooks/useUserProgress';
import { useDeposit } from '@/hooks/useDeposit';
import { useWithdraw } from '@/hooks/useWithdraw';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { getConnection } from '@/lib/solana/anchorClient';
import {
  lamportsToSol,
  solToLamports,
  formatSolBalance,
} from '@/utils/solana/lamportsToSol';
import { TransactionSuccessModal } from '@/components/TransactionSuccessModal';
import { SolBalanceBadge } from '@/components/SolBalanceBadge';

interface UserStatsProps {
  className?: string;
}

export function UserStats({ className }: UserStatsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [isMounted, setIsMounted] = useState(false);
  const [transactionResult, setTransactionResult] = useState<{
    type: 'deposit' | 'withdraw';
    signature: string;
    amount: bigint;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { userProgress, isLoading } = useUserProgress();
  const depositMutation = useDeposit();
  const { address: walletAddress, isConnected } = useWalletAddress();

  // Fetch wallet SOL balance
  const { data: walletBalance = 0 } = useQuery({
    queryKey: ['walletBalance', walletAddress],
    queryFn: async () => {
      if (!walletAddress) return 0;
      const connection = getConnection();
      const balance = await connection.getBalance(new PublicKey(walletAddress));
      return lamportsToSol(BigInt(balance));
    },
    enabled: !!walletAddress && isConnected,
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Track mount state for portal rendering
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // SOL withdrawal hook
  const withdrawMutation = useWithdraw();

  const handleWithdraw = () => {
    const solAmount = parseFloat(withdrawAmount || '0');
    if (solAmount < 0.001) {
      toast.error('Minimum withdrawal is 0.001 Credits');
      return;
    }

    // Use string-based conversion to avoid floating point rounding errors
    const solAmountLamports = solToLamports(withdrawAmount);
    withdrawMutation.mutate(solAmountLamports, {
      onSuccess: data => {
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        // Show success modal with transaction details
        setTransactionResult({
          type: 'withdraw',
          signature: data.signature,
          amount: data.amount,
        });
      },
    });
  };

  const handleDeposit = () => {
    const solAmount = parseFloat(depositAmount || '0');
    if (solAmount < 0.001) {
      toast.error('Minimum deposit is 0.001 Credits');
      return;
    }

    // Use string-based conversion to avoid floating point rounding errors
    const solAmountLamports = solToLamports(depositAmount);
    depositMutation.mutate(solAmountLamports, {
      onSuccess: data => {
        setShowDepositModal(false);
        setDepositAmount('');
        // Show success modal with transaction details
        setTransactionResult({
          type: 'deposit',
          signature: data.signature,
          amount: data.amount,
        });
      },
    });
  };

  // Calculate values for display using BigInt for precision
  // IMPORTANT: chest_credits is the TOTAL (burning + deposits) that can be used for chests AND for withdrawals
  const withdrawableBalance = lamportsToSol(
    BigInt(userProgress?.chest_credits || '0')
  );
  const withdrawSol = parseFloat(withdrawAmount || '0');
  const depositSol = parseFloat(depositAmount || '0');

  // Don't show if wallet not connected
  if (!userProgress || isLoading) {
    return (
      <div className={cn('flex items-center space-x-2 opacity-50', className)}>
        <div className="bg-muted h-8 w-16 animate-pulse rounded" />
      </div>
    );
  }

  return (
    <>
      <div className={cn('relative', className)} ref={dropdownRef}>
        {/* Credits Display */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex items-center space-x-3 rounded-lg border px-4 py-2 transition-all duration-200',
            'backdrop-blur-sm hover:opacity-90',
            'text-sm font-medium max-md:h-[60px]'
          )}
          style={{
            backgroundColor: '#343434',
            borderColor: '#50E5F8',
            color: 'white',
          }}
        >
          <div className="flex items-center space-x-2">
            {/* Credits Icon */}
            <img
              src="/solana-sol-logo.svg"
              alt="Credits"
              width="16"
              height="16"
              className="flex-shrink-0"
            />
            <div className="text-left">
              <div
                className="font-semibold"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 600,
                  color: 'white',
                }}
              >
                {formatSolBalance(BigInt(userProgress?.chest_credits || '0'))}{' '}
                Credits
              </div>
            </div>
          </div>

          <ArrowUpDown
            className="h-3 w-3"
            style={{ color: 'white', opacity: 0.7 }}
          />
        </button>

        {isOpen && (
          <div
            className="absolute top-full right-0 z-[100] mt-2 w-80 rounded-lg p-4 shadow-xl"
            style={{
              backgroundColor: '#343434',
              border: '2px solid #50E5F8',
              boxShadow: '0 0 20px rgba(80, 228, 249, 0.3)',
            }}
          >
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
              <h3
                className="font-semibold"
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  color: 'white',
                }}
              >
                CREDITS
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded p-1"
                style={{ color: 'white' }}
                onMouseEnter={e =>
                  (e.currentTarget.style.backgroundColor =
                    'rgba(255, 255, 255, 0.1)')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.backgroundColor = 'transparent')
                }
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Balance Display */}
            <div className="mb-6 space-y-3">
              <div
                className="rounded-lg p-4"
                style={{
                  backgroundColor: 'rgba(80, 228, 249, 0.1)',
                  border: '1px solid rgba(80, 228, 249, 0.3)',
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <div
                        className="text-sm"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          color: '#50E5F8',
                        }}
                      >
                        Your Credits
                      </div>
                      <div className="rounded bg-green-500/20 px-2 py-0.5 text-xs font-medium text-green-500">
                        TOTAL
                      </div>
                    </div>
                    <div
                      className="text-lg font-bold"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: 'white',
                      }}
                    >
                      {formatSolBalance(
                        BigInt(userProgress?.chest_credits || '0')
                      )}{' '}
                      <span
                        style={{
                          fontFamily: 'Hyperspace Race Capsule, cursive',
                        }}
                      >
                        Credits
                      </span>
                    </div>
                  </div>
                  <img
                    src="/solana-sol-logo.svg"
                    alt="Credits"
                    width="24"
                    height="24"
                    className="flex-shrink-0"
                  />
                </div>
              </div>

              <div
                className="rounded p-2 text-xs"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div className="mb-2">
                  ℹ️ <strong>How Credits Work:</strong>
                </div>
                <div>
                  • <strong>Earn credits</strong> by burning tokens/NFTs
                </div>
                <div>
                  • <strong>Add credits</strong> by depositing SOL
                </div>
                <div>
                  • <strong>Use credits</strong> to open chests for prizes
                </div>
                <div>
                  • <strong>Withdraw credits</strong> back to SOL anytime
                </div>
                <div className="mt-2">1 Credit = 1 SOL</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Deposit Button - NEW */}
              <button
                onClick={() => setShowDepositModal(true)}
                className={cn(
                  'w-full rounded-lg px-4 py-2 font-medium transition-colors hover:opacity-90'
                )}
                style={{
                  backgroundColor: '#FEBB01',
                  color: 'black',
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                }}
              >
                DEPOSIT SOL FOR CREDITS
              </button>

              {/* Withdraw Button */}
              <button
                onClick={() => setShowWithdrawModal(true)}
                disabled={withdrawableBalance < 0.001}
                className={cn(
                  'w-full rounded-lg px-4 py-2 font-medium transition-colors',
                  withdrawableBalance >= 0.001
                    ? 'hover:opacity-90'
                    : 'cursor-not-allowed opacity-50'
                )}
                style={{
                  backgroundColor: '#50E5F8',
                  color: 'black',
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                }}
              >
                WITHDRAW CREDITS FOR SOL
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Withdraw Modal */}
      {isMounted &&
        showWithdrawModal &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => setShowWithdrawModal(false)}
            />

            {/* Modal */}
            <div
              className="relative w-full max-w-md transform rounded-xl shadow-2xl transition-all"
              style={{
                backgroundColor: '#343434',
                border: '2px solid #50E5F8',
                boxShadow: '0 0 20px rgba(80, 228, 249, 0.3)',
              }}
            >
              {/* Header */}
              <div
                className="flex items-center justify-between p-6"
                style={{ borderBottom: '2px solid #50E5F8' }}
              >
                <h3
                  className="text-lg font-semibold"
                  style={{
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                    color: 'white',
                  }}
                >
                  WITHDRAW CREDITS FOR SOL
                </h3>
                <button
                  onClick={() => setShowWithdrawModal(false)}
                  className="rounded-lg p-2 transition-colors"
                  style={{ color: 'white' }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor =
                      'rgba(255, 255, 255, 0.1)')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = 'transparent')
                  }
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-4 p-6">
                <div
                  className="rounded-lg p-4"
                  style={{
                    backgroundColor: 'rgba(80, 228, 249, 0.1)',
                    border: '1px solid rgba(80, 228, 249, 0.3)',
                  }}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <div
                      className="text-sm"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: '#50E5F8',
                      }}
                    >
                      Withdrawable Balance
                    </div>
                    <SolBalanceBadge location="pda" />
                  </div>
                  <div
                    className="text-lg font-bold"
                    style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}
                  >
                    {formatSolBalance(
                      BigInt(userProgress?.chest_credits || '0')
                    )}{' '}
                    <span
                      style={{ fontFamily: 'Hyperspace Race Capsule, cursive' }}
                    >
                      Credits
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-sm font-medium"
                    style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}
                  >
                    Amount to Withdraw
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      min="0.001"
                      max={withdrawableBalance}
                      value={withdrawAmount}
                      onChange={e => setWithdrawAmount(e.target.value)}
                      placeholder="0.0000"
                      className="w-full rounded-lg p-3 pr-24 text-lg"
                      style={{
                        border: withdrawSol > withdrawableBalance ? '2px solid #EF4444' : '2px solid #50E5F8',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        fontFamily: 'Outfit, sans-serif',
                        color: withdrawSol > withdrawableBalance ? '#EF4444' : 'white',
                      }}
                    />
                    <span
                      className="pointer-events-none absolute top-3 right-3"
                      style={{
                        fontFamily: 'Hyperspace Race Capsule, cursive',
                        color: '#50E5F8',
                      }}
                    >
                      Credits
                    </span>
                  </div>
                  <div
                    className="text-xs"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    Minimum withdrawal: 0.001{' '}
                    <span
                      style={{ fontFamily: 'Hyperspace Race Capsule, cursive' }}
                    >
                      Credits
                    </span>
                  </div>

                  {/* Validation Error Messages */}
                  {withdrawSol > 0 && withdrawSol < 0.001 && (
                    <div
                      className="rounded p-2 text-xs"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: '#EF4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                      }}
                    >
                      The minimum withdrawable Credits amount is 0.001.
                    </div>
                  )}
                  {withdrawSol > withdrawableBalance && (
                    <div
                      className="rounded p-2 text-xs"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: '#EF4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                      }}
                    >
                      The available Credits balance in your current Junk Fun account is insufficient for withdrawal.
                    </div>
                  )}
                </div>

                {withdrawSol > 0 && withdrawSol >= 0.001 && (
                  <div
                    className="rounded-lg p-3"
                    style={{
                      backgroundColor: 'rgba(80, 228, 249, 0.1)',
                      border: '2px solid #50E5F8',
                    }}
                  >
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: 'white',
                          }}
                        >
                          Withdrawing:
                        </span>
                        <span
                          className="font-medium"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: '#50E5F8',
                          }}
                        >
                          {withdrawSol.toFixed(4)}{' '}
                          <span
                            style={{
                              fontFamily: 'Hyperspace Race Capsule, cursive',
                            }}
                          >
                            Credits
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: 'white',
                          }}
                        >
                          From:
                        </span>
                        <SolBalanceBadge location="pda" />
                      </div>
                      <div className="flex items-center justify-between">
                        <span
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: 'white',
                          }}
                        >
                          To:
                        </span>
                        <SolBalanceBadge location="wallet" />
                      </div>
                      <div
                        className="flex justify-between"
                        style={{
                          opacity: 0.7,
                          paddingTop: '4px',
                          borderTop: '1px solid rgba(80, 228, 249, 0.2)',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: 'white',
                          }}
                        >
                          USD Value:
                        </span>
                        <span
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: 'white',
                          }}
                        >
                          <span style={{ fontFamily: 'Outfit, sans-serif' }}>
                            ~$
                          </span>
                          {(withdrawSol * 200).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div
                className="p-6"
                style={{ borderTop: '2px solid #50E5F8' }}
              >
                <button
                  onClick={handleWithdraw}
                  disabled={
                    withdrawMutation.isPending ||
                    withdrawSol < 0.001 ||
                    withdrawSol > withdrawableBalance
                  }
                  className="flex w-full items-center justify-center space-x-2 rounded-lg px-4 py-3 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    backgroundColor: '#50E5F8',
                    color: 'black',
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                  }}
                >
                  {withdrawMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>WITHDRAWING...</span>
                    </>
                  ) : (
                    <span>Withdraw Credits</span>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Deposit Modal */}
      {isMounted &&
        showDepositModal &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => setShowDepositModal(false)}
            />

            {/* Modal */}
            <div
              className="relative w-full max-w-md transform rounded-xl shadow-2xl transition-all"
              style={{
                backgroundColor: '#343434',
                border: '2px solid #FEBB01',
                boxShadow: '0 0 20px rgba(254, 187, 1, 0.3)',
              }}
            >
              {/* Header */}
              <div
                className="flex items-center justify-between p-6"
                style={{ borderBottom: '2px solid #FEBB01' }}
              >
                <h3
                  className="text-lg font-semibold"
                  style={{
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                    color: 'white',
                  }}
                >
                  DEPOSIT SOL FOR CREDITS
                </h3>
                <button
                  onClick={() => setShowDepositModal(false)}
                  className="rounded-lg p-2 transition-colors"
                  style={{ color: 'white' }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor =
                      'rgba(255, 255, 255, 0.1)')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = 'transparent')
                  }
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-4 p-6">
                <div
                  className="rounded-lg p-4"
                  style={{
                    backgroundColor: 'rgba(254, 187, 1, 0.1)',
                    border: '1px solid rgba(254, 187, 1, 0.3)',
                  }}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <div
                      className="text-sm"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: '#FEBB01',
                      }}
                    >
                      Current Credits
                    </div>
                    <SolBalanceBadge location="pda" />
                  </div>
                  <div
                    className="text-lg font-bold"
                    style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}
                  >
                    {formatSolBalance(
                      BigInt(userProgress?.chest_credits || '0')
                    )}{' '}
                    <span
                      style={{ fontFamily: 'Hyperspace Race Capsule, cursive' }}
                    >
                      Credits
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-sm font-medium"
                    style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}
                  >
                    Amount to Deposit
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      min="0.001"
                      value={depositAmount}
                      onChange={e => setDepositAmount(e.target.value)}
                      placeholder="0.0000"
                      className="w-full rounded-lg p-3 pr-24 text-lg"
                      style={{
                        border: depositSol > walletBalance ? '2px solid #EF4444' : '2px solid #FEBB01',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        fontFamily: 'Outfit, sans-serif',
                        color: depositSol > walletBalance ? '#EF4444' : 'white',
                      }}
                    />
                    <span
                      className="pointer-events-none absolute top-3 right-3"
                      style={{
                        fontFamily: 'Hyperspace Race Capsule, cursive',
                        color: '#FEBB01',
                      }}
                    >
                      Credits
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div
                      className="text-xs"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      Minimum deposit: 0.001{' '}
                      <span
                        style={{ fontFamily: 'Hyperspace Race Capsule, cursive' }}
                      >
                        Credits
                      </span>
                    </div>
                    <div
                      className="flex items-center gap-1 text-xs"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      <Wallet className="h-3 w-3" />
                      <span>{walletBalance.toFixed(4)}</span>
                    </div>
                  </div>

                  {/* Validation Error Messages */}
                  {depositSol > 0 && depositSol < 0.001 && (
                    <div
                      className="rounded p-2 text-xs"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: '#EF4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                      }}
                    >
                      The minimum deposit amount for SOL is 0.001.
                    </div>
                  )}
                  {depositSol > walletBalance && (
                    <div
                      className="rounded p-2 text-xs"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: '#EF4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                      }}
                    >
                      The SOL balance under your current wallet address is insufficient for deposit.
                    </div>
                  )}
                </div>

                {depositSol > 0 && depositSol >= 0.001 && (
                  <div
                    className="rounded-lg p-3"
                    style={{
                      backgroundColor: 'rgba(254, 187, 1, 0.1)',
                      border: '2px solid #FEBB01',
                    }}
                  >
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: 'white',
                          }}
                        >
                          Depositing:
                        </span>
                        <span
                          className="font-medium"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: '#FEBB01',
                          }}
                        >
                          {depositSol.toFixed(4)}{' '}
                          <span
                            style={{
                              fontFamily: 'Hyperspace Race Capsule, cursive',
                            }}
                          >
                            Credits
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: 'white',
                          }}
                        >
                          From:
                        </span>
                        <SolBalanceBadge location="wallet" />
                      </div>
                      <div className="flex items-center justify-between">
                        <span
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: 'white',
                          }}
                        >
                          To:
                        </span>
                        <SolBalanceBadge location="pda" />
                      </div>
                      <div
                        className="flex justify-between"
                        style={{
                          opacity: 0.7,
                          paddingTop: '4px',
                          borderTop: '1px solid rgba(254, 187, 1, 0.2)',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: 'white',
                          }}
                        >
                          New Credits:
                        </span>
                        <span
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            color: 'white',
                          }}
                        >
                          {(withdrawableBalance + depositSol).toFixed(4)}{' '}
                          <span
                            style={{
                              fontFamily: 'Hyperspace Race Capsule, cursive',
                            }}
                          >
                            Credits
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className="rounded p-3 text-xs"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    color: '#FEBB01',
                    backgroundColor: 'rgba(254, 187, 1, 0.05)',
                    border: '1px solid rgba(254, 187, 1, 0.2)',
                  }}
                >
                  ℹ️ Deposited SOL becomes Credits that can be used to open
                  chests or withdrawn back to your wallet anytime.
                </div>
              </div>

              {/* Footer */}
              <div
                className="p-6"
                style={{ borderTop: '2px solid #FEBB01' }}
              >
                <button
                  onClick={handleDeposit}
                  disabled={depositMutation.isPending || depositSol < 0.001 || depositSol > walletBalance}
                  className="flex w-full items-center justify-center space-x-2 rounded-lg px-4 py-3 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    backgroundColor: '#FEBB01',
                    color: 'black',
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                  }}
                >
                  {depositMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>DEPOSITING...</span>
                    </>
                  ) : (
                    <span>Deposit SOL for Credits</span>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Transaction Success Modal */}
      {transactionResult && (
        <TransactionSuccessModal
          type={transactionResult.type}
          signature={transactionResult.signature}
          amount={transactionResult.amount}
          onClose={() => setTransactionResult(null)}
        />
      )}
    </>
  );
}
